import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography
} from '@mui/material';

import useEmailEditorStore from '../../utilities/emailEditorStore';
import { BLOCK_TYPES } from '../../utilities/blockTypes';
import ColumnBlock from './ColumnBlock';
import EmailEditorBlock from './EmailEditorBlock';

function ColumnContainerBlock({ id, type, properties, children = [] }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  const { 
    addBlock, 
    selectBlock, 
    selectedBlockId, 
    removeBlock, 
    updateBlockProperties,
    getBlockById,
    getChildrenBlocks,
    globalProperties,
  } = useEmailEditorStore();
  
  const isSelected = selectedBlockId === id;

  // Helper function to get gap value consistently
  const getGapValue = (gap) => {
    console.log(`Getting gap value for:`, gap, typeof gap);
    if (typeof gap === 'number') {
      return `${gap}px`;
    }
    if (typeof gap === 'string' && gap.trim() !== '') {
      return gap;
    }
    return '10px'; // default value
  };

  // Helper function to extract numeric value from gap string
  const getGapNumericValue = (gapValue) => {
    if (typeof gapValue === 'number') {
      return gapValue;
    }
    if (typeof gapValue === 'string') {
      const numericValue = parseFloat(gapValue.replace(/[^\d.]/g, ''));
      return isNaN(numericValue) ? 10 : numericValue;
    }
    return 10;
  };

  // Calculate column widths accounting for gap
  const calculateColumnWidths = (columnCount, gapValue) => {
    const gapNumeric = getGapNumericValue(gapValue);
    const totalGapSpace = (columnCount - 1) * gapNumeric; // Total space taken by gaps
    const availableWidth = 100; // Working in percentages
    
    // Convert gap to percentage of container width (assuming a base width for calculation)
    // This is an approximation - in reality, you might want to get the actual container width
    const containerWidth = globalProperties.emailWidth; // Assuming a typical email width, adjust as needed
    const gapPercentage = (totalGapSpace / containerWidth) * 100;
    
    const availableWidthForColumns = availableWidth - gapPercentage;
    const columnWidth = Math.max(0, availableWidthForColumns / columnCount);
    
    console.log(`Calculating widths - Columns: ${columnCount}, Gap: ${gapNumeric}px, Column width: ${columnWidth.toFixed(2)}%`);
    
    return `${columnWidth.toFixed(2)}%`;
  };

  // Update column widths based on the number of columns and gap
  const updateColumnWidths = (columnCount, gapValue) => {
    let allChildren = getChildrenBlocks(id);
    const currentColumns = allChildren.filter(child => child.type === BLOCK_TYPES.COLUMN);
    const calculatedWidth = calculateColumnWidths(columnCount, gapValue);
  
    currentColumns.forEach((column, index) => {
      const customWidthKey = `column${index + 1}width`;
      updateBlockProperties(id, {[customWidthKey]: calculatedWidth});
      updateBlockProperties(column.id, { width: calculatedWidth });
    });
  };

  // Ensure we have the correct number of columns
  useEffect(() => {
    const currentColumns = children.filter(child => child.type === BLOCK_TYPES.COLUMN);
    const targetColumnCount = properties.columnnumbers || 3;
    
    if (currentColumns.length !== targetColumnCount) {
      // Add columns if we need more
      if (currentColumns.length < targetColumnCount) {
        const columnsToAdd = targetColumnCount - currentColumns.length;
        for (let i = 0; i < columnsToAdd; i++) {
          console.log("Adding column:", i);
          addBlock(BLOCK_TYPES.COLUMN, id);
        }
      }
      // Remove excess columns if we have too many
      else if (currentColumns.length > targetColumnCount) {
        const columnsToRemove = currentColumns.slice(targetColumnCount);
        columnsToRemove.forEach(column => {
          removeBlock(column.id);
        });
      }
    }
    
    // Always update column widths when column count changes
    const gapValue = getGapValue(properties.columngap);
    updateColumnWidths(targetColumnCount, gapValue);
  }, [properties.columnnumbers, children.length]);

  // Update column widths when gap changes
  useEffect(() => {
    const currentColumns = children.filter(child => child.type === BLOCK_TYPES.COLUMN);
    const columnCount = properties.columnnumbers || 3;
    const gapValue = getGapValue(properties.columngap);
    
    if (currentColumns.length > 0) {
      console.log(`Gap changed to ${gapValue}, updating column widths...`);
      updateColumnWidths(columnCount, gapValue);
    }
  }, [properties.columngap]);

  const handleAddClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAddChild = (childType) => {
    if (childType !== BLOCK_TYPES.COLUMN) {
      addBlock(childType, id);
    }
    handleClose();
  };

  const handleContainerClick = (event) => {
    event.stopPropagation();
    selectBlock(id);
  };

  const handleDeleteContainer = (event) => {
    event.stopPropagation();
    removeBlock(id);
  };

  // Convert padding object to CSS string
  const getPaddingStyle = (padding) => {
    if (typeof padding === 'object') {
      return `${padding.top || 0}px ${padding.right || 0}px ${padding.bottom || 0}px ${padding.left || 0}px`;
    }
    return padding || '10px';
  };

  // Convert margin object to CSS string
  const getMarginStyle = (margin) => {
    if (typeof margin === 'object') {
      return `${margin.top || 0}px ${margin.right || 0}px ${margin.bottom || 0}px ${margin.left || 0}px`;
    }
    return margin || '0px';
  };

  // Convert border object to CSS string
  const getBorderStyle = (border) => {
    if (typeof border === 'object') {
      return `${border.width || 0}px ${border.style || 'solid'} ${border.color || '#000000'}`;
    }
    return border || 'none';
  };

  // Separate columns from other children
  const columns = children.filter(child => child.type === BLOCK_TYPES.COLUMN);
  const otherChildren = children.filter(child => child.type !== BLOCK_TYPES.COLUMN);

  const isEmpty = children.length === 0;

  // Get the gap value for consistent use
  const gapValue = getGapValue(properties.columngap);
  
  // Log the final gap value being used
  console.log(`ColumnContainer ${id} - Final gap value being applied:`, gapValue);

  return (
    <Box
      onClick={handleContainerClick}
      sx={{
        backgroundColor: properties.backgroundColor || 'transparent',
        padding: getPaddingStyle(properties.padding),
        margin: getMarginStyle(properties.margin),
        borderRadius: `${properties.borderRadius || 0}px`,
        border: getBorderStyle(properties.border),
        minHeight: `${properties.minHeight || 100}px`,
        width: properties.width || '100%',
        maxWidth: properties.maxWidth || '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        outline: isSelected ? '2px solid #1976d2' : 'none',
        outlineOffset: '2px',
        transition: 'outline 0.2s ease-in-out',
        '&:hover': {
          outline: isSelected ? '2px solid #1976d2' : '1px dashed #ccc',
        },
        '&:hover .container-action-buttons': {
          opacity: 1
        }
      }}
    >
      {isEmpty ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: `${properties.minHeight || 100}px`,
            flexGrow: 1,
            border: '2px dashed #e0e0e0',
            borderRadius: '4px',
            color: '#666',
            backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.05)' : 'transparent'
          }}
        >
          <Typography variant="body2" color="textSecondary" align="center">
            Column Container Block
            <br />
            {properties.columnnumbers || 3} Columns
            <br />
            Gap: {gapValue}
            <br />
            Click + to add content
          </Typography>
        </Box>
      ) : (
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column', 
          gap: gapValue,
          width: '100%' 
        }}>
          {/* Columns Row */}
          {columns.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: gapValue,
                width: '100%',
                alignItems: properties.alignItems || 'center',
               
              }}
            >
              {columns.map((column) => (
                <Box
                  key={column.id}
                  sx={{ 
                    width: column.properties.width || 'auto', 
                    flexShrink: 0,
                    margin: 0,
                    // Prevent column from exceeding its allocated space
                    minWidth: 0,
                    boxSizing: 'border-box',
                  }}
                >
                  <ColumnBlock
                    id={column.id}
                    type={column.type}
                    properties={column.properties}
                    children={column.children}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Enhanced debug info */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            left: 8,
            backgroundColor: '#1976d2',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            zIndex: 11
          }}
        >
          {properties.columnnumbers || 3} Columns
        </Box>
      )}
    </Box>
  );
}

export default ColumnContainerBlock;